import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import QuickLogTimeModal from "../../components/QuickLogTimeModal";
import FieldLogTab from "./FieldLog";
import MoistureLogTab from "./MoistureLog";
import { TasksTab, PhotosTab, AiSummaryCard, HealthBadge, ProgressControl, FieldDocsTab } from "./ProjectExtras";
import { InvoicesPanel } from "./InvoicesPanel";
import { BillingStatusBadge } from "../../components/BillingStatusBadge";
import {
    ArrowLeft, Plus, Trash, FloppyDisk, CircleNotch, Clock,
    CalendarBlank, Truck, Receipt, MapPin, Kanban, Paperclip, UploadSimple,
    Calculator, FileArrowUp, ArrowSquareOut, CheckCircle, ListChecks, ImageSquare, Sparkle,
    QrCode, X, Printer, ArrowsClockwise, ClipboardText, Copy, Drop, Buildings,
} from "@phosphor-icons/react";

const STAGE_LABELS = {
    rom: "ROM / Quote", review: "Adjustor / Insurer Review", go_no_go: "Go / No-Go",
    execution: "Execution (WIP)", invoice: "Invoice", pay: "Payment", ar: "AR / Closed",
};
const STAGES = ["rom", "review", "go_no_go", "execution", "invoice", "pay", "ar"];

const TABS = [
    { key: "overview", label: "Overview", icon: Kanban },
    { key: "ai_review", label: "AI review", icon: Sparkle },
    { key: "rom", label: "ROM", icon: Calculator },
    { key: "tasks", label: "Tasks", icon: ListChecks },
    { key: "phases", label: "Phases", icon: CalendarBlank },
    { key: "photos", label: "Photos", icon: ImageSquare },
    { key: "field_docs", label: "Field Documentation", icon: Buildings },
    { key: "field_log", label: "Field Log", icon: ClipboardText },
    { key: "moisture_log", label: "Moisture Log", icon: Drop },
    { key: "sub_invoices", label: "Sub-con invoices", icon: Receipt },
    { key: "equipment_hire", label: "Equipment hire", icon: Truck },
    { key: "timesheets", label: "Timesheets", icon: Clock },
    { key: "site_visits", label: "Site visits", icon: MapPin },
    { key: "final_invoice", label: "Invoicing", icon: FileArrowUp },
];

const CHILD_COLS = {
    phases: [
        { k: "name", label: "Phase", type: "text" },
        { k: "start_date", label: "Start", type: "date" },
        { k: "days", label: "Days", type: "number" },
        { k: "status", label: "Status", type: "select", opts: ["not_started", "in_progress", "complete"] },
    ],
    sub_invoices: [
        { k: "vendor", label: "Vendor", type: "text" },
        { k: "description", label: "Description", type: "text" },
        { k: "amount", label: "Amount", type: "number" },
        { k: "date", label: "Date", type: "date" },
        { k: "status", label: "Status", type: "select", opts: ["pending", "received", "paid"] },
        { k: "file_id", label: "Invoice file", type: "file" },
    ],
    equipment_hire: [
        { k: "item", label: "Item", type: "text" },
        { k: "vendor", label: "Vendor", type: "text" },
        { k: "qty", label: "Qty", type: "number" },
        { k: "rate", label: "Day rate", type: "number" },
        { k: "start_date", label: "From", type: "date" },
        { k: "end_date", label: "To", type: "date" },
    ],
    site_visits: [
        { k: "date", label: "Date", type: "date" },
        { k: "purpose", label: "Purpose", type: "text" },
        { k: "assigned_to", label: "Assigned to", type: "text" },
        { k: "status", label: "Status", type: "select", opts: ["scheduled", "done", "cancelled"] },
        { k: "notes", label: "Notes", type: "text" },
    ],
};

export default function ProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [project, setProject] = useState(null);
    const [tab, setTab] = useState("overview");
    const [err, setErr] = useState("");
    const [showLog, setShowLog] = useState(false);
    const [showSiteQr, setShowSiteQr] = useState(false);

    const load = useCallback(async () => {
        try { const { data } = await endpoints.project(id); setProject(data); }
        catch (e) { setErr(formatApiError(e)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (err) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-red-600">{err}</div>;
    if (!project) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-text-3)]">Loading…</div>;

    async function patch(fields) {
        const { data } = await endpoints.updateProject(id, fields);
        setProject(data);
        return data;
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-5" data-testid="project-detail-page">
            <div className="no-print space-y-5">
            <button onClick={() => navigate("/projects")} className="text-sm text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1.5">
                <ArrowLeft size={14} weight="bold" /> Pipeline
            </button>

            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-[11px] font-mono text-[var(--rom-text-3)] flex items-center gap-2">
                        {project.project_number}
                    </div>
                    <h1 className="font-display font-black text-3xl tracking-tight">{project.name}</h1>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="chip chip-navy capitalize">{project.loss_type || "—"}</span>
                        <HealthBadge health={project.health} />
                        <BillingStatusBadge status={project.billing_status} />
                        {project.address && <span className="text-sm text-[var(--rom-text-2)] flex items-center gap-1"><MapPin size={12} /> {project.address}</span>}
                    </div>
                    <div className="mt-3"><ProgressControl project={project} onSave={patch} /></div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1">Stage</label>
                        <select data-testid="stage-select" value={project.stage} onChange={(e) => patch({ stage: e.target.value })}
                            className="h-9 px-3 rounded-[4px] border border-[var(--rom-border-strong)] text-sm font-semibold bg-white">
                            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                        </select>
                    </div>
                    <button onClick={() => setShowLog(true)} className="btn btn-primary mt-5" data-testid="project-log-time-btn"><Clock size={14} weight="bold" /> Log time</button>
                    <button onClick={() => setShowSiteQr(true)} className="btn btn-ghost mt-5" data-testid="project-site-qr-btn"><QrCode size={14} weight="bold" /> Site QR</button>
                </div>
            </header>

            {/* P&L strip */}
            <PnlStrip project={project} onSave={patch} canEdit={isManage} />

            {/* Tabs */}
            <div className="flex gap-1 border-b border-[var(--rom-border)] overflow-x-auto scroll-fade-x" data-testid="project-detail-tabs">
                {TABS.map((t) => (
                    <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-[var(--rom-orange)] text-[var(--rom-orange-dark)]" : "border-transparent text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"}`}>
                        <t.icon size={15} weight="bold" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview" && <Overview project={project} onSave={patch} canEdit={isManage} />}
            {tab === "ai_review" && <AiSummaryCard project={project} onUpdated={setProject} />}
            {tab === "rom" && <RomTab project={project} navigate={navigate} />}
            {tab === "tasks" && <TasksTab project={project} onSave={patch} canEdit={isManage} />}
            {tab === "photos" && <PhotosTab project={project} onSave={patch} canEdit={isManage} />}
            {tab === "field_docs" && <FieldDocsTab project={project} />}
            {tab === "field_log" && <FieldLogTab project={project} />}
            {tab === "moisture_log" && <MoistureLogTab project={project} />}
            {tab === "timesheets" && <TimesheetsTab project={project} onLog={() => setShowLog(true)} />}
            {tab === "final_invoice" && <FinalInvoiceTab project={project} onSave={patch} canEdit={isManage} onChanged={load} />}
            {tab === "phases" && <PhaseGantt phases={project.phases} project={project} />}
            {CHILD_COLS[tab] && <ChildTable project={project} field={tab} cols={CHILD_COLS[tab]} onSave={patch} canEdit={isManage} />}
            </div>

            {showLog && <QuickLogTimeModal project={project} onClose={() => setShowLog(false)} onChanged={load} />}
            {showSiteQr && <SiteQrModal project={project} onClose={() => setShowSiteQr(false)} />}
        </div>
    );
}

function SiteQrModal({ project, onClose }) {
    const [regenerating, setRegenerating] = useState(false);
    const [bust, setBust] = useState(0);
    const [token, setToken] = useState(project.site_qr_token);
    const [copied, setCopied] = useState(false);

    const shareLink = `${window.location.origin}/clock-in?code=BR-SITE:${token}`;

    async function regenerate() {
        if (!window.confirm("Regenerate this site QR? The old code posted on site will stop working.")) return;
        setRegenerating(true);
        try {
            const { data } = await endpoints.regenerateSiteToken(project.id);
            setToken(data.site_qr_token);
            setBust((b) => b + 1);
        }
        catch (e) { alert(formatApiError(e)); }
        finally { setRegenerating(false); }
    }

    function copyLink() {
        navigator.clipboard.writeText(shareLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 print:bg-transparent print:static print:block flex items-center justify-center p-4" onClick={onClose}>
            <div className="no-print bg-white rounded-lg w-full max-w-xs shadow-xl text-center" onClick={(e) => e.stopPropagation()} data-testid="site-qr-modal">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-base">Site QR &amp; link</h3>
                    <button onClick={onClose} className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <img src={`${endpoints.projectQrUrl(project.id)}&v=${bust}`} alt="Site QR" className="mx-auto w-48 h-48 border border-[var(--rom-border)] rounded" data-testid="site-qr-image" />
                    <p className="text-xs text-[var(--rom-text-3)]">Print and post this at the job site, or share the link below. Either one auto-detects <b>{project.name}</b> and captures time against it.</p>
                    <div className="flex gap-1.5">
                        <input readOnly value={shareLink} onClick={(e) => e.target.select()} data-testid="site-share-link-input"
                            className="flex-1 h-9 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs font-mono bg-slate-50 truncate" />
                        <button onClick={copyLink} data-testid="site-share-link-copy" className="btn btn-ghost shrink-0 px-3">
                            {copied ? <CheckCircle size={14} weight="bold" className="text-green-600" /> : <Copy size={14} weight="bold" />} {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => window.print()} className="btn btn-ghost flex-1 justify-center" data-testid="site-qr-print"><Printer size={14} weight="bold" /> Print poster</button>
                        <button onClick={regenerate} disabled={regenerating} className="btn btn-ghost flex-1 justify-center text-red-600" data-testid="site-qr-regenerate">
                            {regenerating ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />} Regenerate
                        </button>
                    </div>
                </div>
            </div>

            {/* Printable full-page site-entry poster (screen-hidden, print-only) */}
            <div className="hidden print:block print-area" data-testid="site-poster">
                <div className="max-w-xl mx-auto text-center py-10">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 font-semibold">Beyond Restoration · Site Entry</div>
                    <h1 className="font-display font-black text-4xl tracking-tight mt-2">{project.name}</h1>
                    <div className="text-sm text-gray-600 mt-1">{project.project_number}{project.address ? ` · ${project.address}` : ""}</div>
                    <img src={`${endpoints.projectQrUrl(project.id)}&v=${bust}`} alt="Site QR" className="mx-auto w-64 h-64 mt-8 border-2 border-black" />
                    <div className="font-display font-black text-2xl mt-4">Scan this code to clock in</div>

                    <div className="grid grid-cols-2 gap-8 mt-10 text-left">
                        <div>
                            <div className="font-display font-black text-lg border-b-2 border-black pb-1 mb-2">Employees</div>
                            <ol className="list-decimal list-inside text-sm space-y-1.5">
                                <li>Open your phone's camera and point it at this code</li>
                                <li>Tap the link that appears — opens straight away, no login</li>
                                <li>Scan or select your badge, describe your work, and clock in</li>
                            </ol>
                        </div>
                        <div>
                            <div className="font-display font-black text-lg border-b-2 border-black pb-1 mb-2">Subcontractors</div>
                            <ol className="list-decimal list-inside text-sm space-y-1.5">
                                <li>Open your phone's camera and point it at this code</li>
                                <li>Tap the link that appears — no account needed</li>
                                <li>Sign in with your email, company &amp; work type, then clock in</li>
                            </ol>
                        </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-10">Questions? Ask your site supervisor. · Remember to clock out at the end of your shift.</div>
                </div>
            </div>
        </div>
    );
}

function RomTab({ project, navigate }) {
    const pnl = project.pnl || {};
    if (!project.rom_job_id) {
        return (
            <div className="rom-card" data-testid="rom-tab"><div className="rom-card-body text-center py-10">
                <Calculator size={32} className="mx-auto mb-3 text-[var(--rom-text-3)]" />
                <p className="font-semibold">No ROM linked to this project</p>
                <p className="text-xs text-[var(--rom-text-3)] mt-1 max-w-md mx-auto">This is a standalone project. You can build a ROM in the calculator and link it, or keep tracking against the contract value.</p>
                <button onClick={() => navigate("/calculator/new")} className="btn btn-ghost mt-4"><Calculator size={14} weight="bold" /> Open ROM calculator</button>
            </div></div>
        );
    }
    return (
        <div className="rom-card" data-testid="rom-tab">
            <div className="rom-card-body space-y-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">Linked ROM (the original quote)</div>
                        <div className="font-display font-black text-3xl tracking-tight mt-1">{formatCurrency(project.rom_value)}</div>
                        <div className="text-xs text-[var(--rom-text-3)] mt-1">This sq-ft ROM is the basis for the contract value and the P&amp;L revenue.</div>
                    </div>
                    <button onClick={() => navigate(`/calculator/${project.rom_job_id}?project=${project.id}`)} className="btn btn-primary" data-testid="open-rom-btn">
                        <ArrowSquareOut size={14} weight="bold" /> Open full ROM
                    </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-4 border-t border-[var(--rom-border)]">
                    <MiniStat label="ROM value" value={formatCurrency(project.rom_value)} />
                    <MiniStat label="Contract value" value={formatCurrency(project.contract_value)} />
                    <MiniStat label="Cost to date" value={formatCurrency(pnl.cost_total)} accent="orange" />
                    <MiniStat label="ROM vs cost" value={pnl.margin_pct != null ? `${pnl.margin_pct}%` : "—"} accent="navy" />
                </div>
            </div>
        </div>
    );
}

function PhaseGantt({ phases, project }) {
    const rows = (phases || []).filter((p) => p.start_date && p.days);
    if (rows.length === 0) {
        return (
            <div className="rom-card mb-4" data-testid="phase-gantt-empty"><div className="rom-card-body text-center py-6 text-sm text-[var(--rom-text-3)]">
                Add phases with a <b>start date</b> and <b>days</b> below to see the Gantt timeline.
            </div></div>
        );
    }
    const parse = (s) => new Date(s + "T00:00:00");
    const ends = rows.map((p) => { const d = parse(p.start_date); d.setDate(d.getDate() + (Number(p.days) || 1)); return d; });
    const minStart = new Date(Math.min(...rows.map((p) => parse(p.start_date).getTime())));
    const maxEnd = new Date(Math.max(...ends.map((d) => d.getTime())));
    const totalDays = Math.max(1, Math.round((maxEnd - minStart) / 86400000));
    // Week gridlines
    const weeks = [];
    for (let d = new Date(minStart); d <= maxEnd; d.setDate(d.getDate() + 7)) weeks.push(new Date(d));
    const STATUS_COLOR = { complete: "var(--rom-green)", in_progress: "var(--rom-orange)", not_started: "var(--rom-navy)" };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayPct = ((today - minStart) / 86400000) / totalDays * 100;
    const fmt = (d) => `${d.getDate()}/${d.getMonth() + 1}`;

    return (
        <div className="rom-card mb-4" data-testid="phase-gantt">
            <div className="rom-card-head"><h3>Schedule · Gantt</h3><span className="text-xs text-[var(--rom-text-3)]">{fmt(minStart)} → {fmt(maxEnd)} · {totalDays} days</span></div>
            <div className="rom-card-body">
                <div className="relative">
                    {/* week gridlines */}
                    <div className="absolute inset-0 ml-[180px] pointer-events-none">
                        {weeks.map((w, i) => { const left = ((w - minStart) / 86400000) / totalDays * 100; return (
                            <div key={i} className="absolute top-0 bottom-0 border-l border-dashed border-[var(--rom-border)]" style={{ left: `${left}%` }}>
                                <span className="absolute -top-5 -translate-x-1/2 text-[10px] text-[var(--rom-text-3)] whitespace-nowrap">{fmt(w)}</span>
                            </div>
                        ); })}
                        {todayPct >= 0 && todayPct <= 100 && (
                            <div className="absolute top-0 bottom-0 w-[2px] bg-[var(--rom-orange)]/70 z-10" style={{ left: `${todayPct}%` }} title="Today" />
                        )}
                    </div>
                    <div className="space-y-2 pt-5">
                        {rows.map((p, i) => {
                            const start = parse(p.start_date);
                            const leftPct = ((start - minStart) / 86400000) / totalDays * 100;
                            const widthPct = Math.max(2, (Number(p.days) || 1) / totalDays * 100);
                            const color = STATUS_COLOR[p.status] || "var(--rom-navy)";
                            return (
                                <div key={p.id || i} className="flex items-center gap-3" data-testid={`gantt-row-${i}`}>
                                    <div className="w-[168px] shrink-0 text-xs font-medium truncate flex items-center gap-1.5">
                                        {p.status === "complete" && <CheckCircle size={12} weight="fill" className="text-[var(--rom-green)] shrink-0" />}
                                        {p.name || `Phase ${i + 1}`}
                                    </div>
                                    <div className="relative flex-1 h-6">
                                        <div className="absolute h-6 rounded-[4px] flex items-center px-2 text-[10px] font-semibold text-white overflow-hidden whitespace-nowrap"
                                            style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: color, opacity: p.status === "not_started" ? 0.55 : 1 }}
                                            title={`${p.name} · ${p.days}d · ${p.status || "not started"}`}>
                                            {p.days}d
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function FinalInvoiceTab({ project, onSave, canEdit, onChanged }) {
    const [busy, setBusy] = useState(false);
    async function upload(file) {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("project_id", project.id);
            fd.append("kind", "final_invoice");
            const { data } = await endpoints.uploadFile(fd);
            await onSave({ final_invoice_file_id: data.id, final_invoice_file_name: data.original_filename });
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="space-y-4" data-testid="final-invoice-tab">
            <InvoicesPanel project={project} canEdit={canEdit} onChanged={onChanged} />
            <div className="rom-card">
                <div className="rom-card-head"><h3>Final invoice to client / insurer</h3></div>
                <div className="rom-card-body">
                    {project.final_invoice_file_id ? (
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <a href={endpoints.fileDownloadUrl(project.final_invoice_file_id)} target="_blank" rel="noreferrer" data-testid="final-invoice-link"
                                className="inline-flex items-center gap-2 px-4 py-3 rounded-[6px] border border-[var(--rom-border)] hover:border-[var(--rom-orange)] transition-colors">
                                <Receipt size={20} weight="bold" className="text-[var(--rom-orange)]" />
                                <span className="font-medium text-sm">{project.final_invoice_file_name || "Final invoice"}</span>
                                <ArrowSquareOut size={14} className="text-[var(--rom-text-3)]" />
                            </a>
                            {canEdit && (
                                <div className="flex gap-2">
                                    <label className="btn btn-ghost cursor-pointer" data-testid="final-invoice-replace">
                                        {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />} Replace
                                        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
                                    </label>
                                    <button onClick={() => onSave({ final_invoice_file_id: "", final_invoice_file_name: "" })} className="btn btn-ghost text-red-600"><Trash size={14} /> Remove</button>
                                </div>
                            )}
                        </div>
                    ) : canEdit ? (
                        <label className="flex flex-col items-center justify-center gap-2 py-10 border-2 border-dashed border-[var(--rom-border-strong)] rounded-[8px] cursor-pointer hover:border-[var(--rom-orange)] transition-colors" data-testid="final-invoice-upload">
                            {busy ? <CircleNotch size={28} className="animate-spin text-[var(--rom-orange)]" /> : <FileArrowUp size={28} weight="bold" className="text-[var(--rom-text-3)]" />}
                            <span className="text-sm font-semibold">{busy ? "Uploading…" : "Upload the final invoice"}</span>
                            <span className="text-xs text-[var(--rom-text-3)]">PDF, image or Excel · up to 25MB</span>
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" className="hidden" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
                        </label>
                    ) : (
                        <div className="text-center py-8 text-sm text-[var(--rom-text-3)]">No final invoice uploaded yet.</div>
                    )}
                </div>
            </div>
            <div className="rom-card">
                <div className="rom-card-head"><h3>Billing</h3></div>
                <div className="rom-card-body grid grid-cols-2 lg:grid-cols-3 gap-4 max-w-2xl">
                    {project.invoice_count > 0 && (
                        <div className="col-span-2 lg:col-span-3 text-[11px] text-[var(--rom-text-3)] -mb-2">Computed automatically from {project.invoice_count} real invoice{project.invoice_count > 1 ? "s" : ""} above.</div>
                    )}
                    <EditableMini label="Invoiced" value={project.invoiced_value} onSave={(v) => onSave({ invoiced_value: v })} testId="fi-invoiced" disabled={!canEdit || project.invoice_count > 0} />
                    <EditableMini label="Paid" value={project.paid_value} onSave={(v) => onSave({ paid_value: v })} testId="fi-paid" disabled={!canEdit || project.invoice_count > 0} />
                    <MiniStat label="Outstanding AR" value={formatCurrency(Math.max(0, (project.invoiced_value || 0) - (project.paid_value || 0)))} accent="navy" />
                </div>
            </div>
        </div>
    );
}

function Overview({ project, onSave, canEdit }) {
    const [form, setForm] = useState({ start_date: project.start_date || "", target_end_date: project.target_end_date || "", notes: project.notes || "" });
    const [saving, setSaving] = useState(false);
    async function save() { setSaving(true); try { await onSave(form); } finally { setSaving(false); } }
    const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none";
    return (
        <div className="rom-card">
            <div className="rom-card-body grid grid-cols-2 gap-4 max-w-2xl">
                <div><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Start date</label><input data-testid="ov-start" type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className={inputCls} disabled={!canEdit} /></div>
                <div><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Target end date</label><input data-testid="ov-end" type="date" value={form.target_end_date} onChange={(e) => setForm((f) => ({ ...f, target_end_date: e.target.value }))} className={inputCls} disabled={!canEdit} /></div>
                <div className="col-span-2"><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Notes</label><textarea data-testid="ov-notes" rows={4} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none" disabled={!canEdit} /></div>
                {canEdit && <div><button data-testid="ov-save" onClick={save} disabled={saving} className="btn btn-primary">{saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />} Save</button></div>}
            </div>
        </div>
    );
}

function TimesheetsTab({ project, onLog }) {
    const [roll, setRoll] = useState(null);
    useEffect(() => { endpoints.projectLabour(project.id).then((r) => setRoll(r.data)).catch(() => {}); }, [project.id]);
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <ValueCard label="Logged hours" value={null} raw={roll ? `${roll.total_hours}h` : "—"} />
                <ValueCard label="Labour cost" value={roll?.total_cost} />
                <ValueCard label="Approved" value={roll?.approved_cost} />
                <ValueCard label="Pending" value={null} raw={roll ? `${roll.pending}` : "—"} />
            </div>
            <div className="rom-card">
                <div className="rom-card-head"><h3>Time entries on this project</h3><button onClick={onLog} className="btn btn-sm btn-primary" data-testid="ts-tab-log"><Clock size={13} weight="bold" /> Log time</button></div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Date</th><th>Employee</th><th>Description</th><th className="numeric">Hours</th><th className="numeric">Cost</th><th>Status</th></tr></thead>
                        <tbody>
                            {(roll?.entries || []).length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-8 text-[var(--rom-text-3)]">No time logged yet. Use “Log time”.</td></tr>
                            ) : roll.entries.map((e) => (
                                <tr key={e.id} data-testid={`pts-row-${e.id}`}>
                                    <td className="font-mono text-xs">{e.work_date}</td>
                                    <td className="font-semibold">{e.employee_name}</td>
                                    <td className="text-[var(--rom-text-2)]">{e.type_raw || "—"}</td>
                                    <td className="numeric font-semibold">{(e.decimal_hours || 0).toFixed(1)}h</td>
                                    <td className="numeric font-mono">{formatCurrency(e.cost_amount)}</td>
                                    <td><span className={`chip ${e.approval_status === "approved" ? "chip-green" : e.approval_status === "rejected" ? "chip-gray" : "chip-orange"}`}>{e.approval_status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function ChildTable({ project, field, cols, onSave, canEdit }) {
    const [rows, setRows] = useState(project[field] || []);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    useEffect(() => { setRows(project[field] || []); setDirty(false); }, [project, field]);

    function update(i, k, v) { setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r)); setDirty(true); }
    function addRow() { setRows((rs) => [...rs, cols.reduce((o, c) => ({ ...o, [c.k]: c.type === "number" ? 0 : "" }), {})]); setDirty(true); }
    function delRow(i) { setRows((rs) => rs.filter((_, idx) => idx !== i)); setDirty(true); }
    async function save() { setSaving(true); try { await onSave({ [field]: rows }); setDirty(false); } finally { setSaving(false); } }

    async function uploadForRow(i, file) {
        if (!file) return;
        setUploadingIdx(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("project_id", project.id);
            fd.append("kind", field);
            const { data } = await endpoints.uploadFile(fd);
            // attach to row + persist the whole array immediately so the ref isn't lost
            const next = rows.map((r, idx) => idx === i ? { ...r, file_id: data.id, file_name: data.original_filename } : r);
            setRows(next);
            await onSave({ [field]: next });
            setDirty(false);
        } catch (e) { alert(formatApiError(e)); }
        finally { setUploadingIdx(null); }
    }

    return (
        <div className="rom-card" data-testid={`child-${field}`}>
            <div className="rom-card-head">
                <h3 className="capitalize">{field.replace("_", " ")}</h3>
                {canEdit && (
                    <div className="flex gap-2">
                        <button onClick={addRow} className="btn btn-ghost btn-sm" data-testid={`add-${field}`}><Plus size={13} weight="bold" /> Add row</button>
                        {dirty && <button onClick={save} disabled={saving} className="btn btn-primary btn-sm" data-testid={`save-${field}`}>{saving ? <CircleNotch size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="bold" />} Save</button>}
                    </div>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="rom-table">
                    <thead><tr>{cols.map((c) => <th key={c.k} className={c.type === "number" ? "numeric" : ""}>{c.label}</th>)}{canEdit && <th></th>}</tr></thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={cols.length + 1} className="text-center py-8 text-[var(--rom-text-3)]">Nothing yet.{canEdit && " Add a row."}</td></tr>
                        ) : rows.map((r, i) => (
                            <tr key={r.id || i} data-testid={`${field}-row-${i}`}>
                                {cols.map((c) => (
                                    <td key={c.k} className={c.type === "number" ? "numeric" : ""}>
                                        {c.type === "file" ? (
                                            <FileCell row={r} idx={i} canEdit={canEdit} uploading={uploadingIdx === i} onUpload={uploadForRow} onClear={() => update(i, "file_id", "")} />
                                        ) : !canEdit ? (
                                            <span>{c.type === "number" && c.k === "amount" ? formatCurrency(r[c.k]) : (r[c.k] ?? "—")}</span>
                                        ) : c.type === "select" ? (
                                            <select value={r[c.k] || ""} onChange={(e) => update(i, c.k, e.target.value)} className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs bg-white">
                                                <option value="">—</option>
                                                {c.opts.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                                            </select>
                                        ) : (
                                            <input type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"} value={r[c.k] ?? ""} onChange={(e) => update(i, c.k, c.type === "number" ? Number(e.target.value) : e.target.value)}
                                                className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs w-full min-w-[90px]" />
                                        )}
                                    </td>
                                ))}
                                {canEdit && <td><button onClick={() => delRow(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash size={14} /></button></td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FileCell({ row, idx, canEdit, uploading, onUpload, onClear }) {
    if (row.file_id) {
        return (
            <div className="flex items-center gap-1.5" data-testid={`file-cell-${idx}`}>
                <a href={endpoints.fileDownloadUrl(row.file_id)} target="_blank" rel="noreferrer"
                    data-testid={`file-link-${idx}`}
                    className="inline-flex items-center gap-1 text-[var(--rom-orange-dark)] hover:underline text-xs max-w-[140px] truncate">
                    <Paperclip size={12} weight="bold" /> {row.file_name || "view file"}
                </a>
                {canEdit && <button onClick={onClear} className="text-[var(--rom-text-3)] hover:text-red-500" title="Remove"><Trash size={12} /></button>}
            </div>
        );
    }
    if (!canEdit) return <span className="text-[var(--rom-text-3)] text-xs">—</span>;
    return (
        <label className="inline-flex items-center gap-1 px-2 py-1 rounded-[4px] border border-dashed border-[var(--rom-border-strong)] text-xs cursor-pointer hover:border-[var(--rom-orange)] text-[var(--rom-text-2)]" data-testid={`file-upload-${idx}`}>
            {uploading ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />} {uploading ? "Uploading…" : "Upload"}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls" className="hidden" disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onUpload(idx, f); }} />
        </label>
    );
}

function PnlStrip({ project, onSave, canEdit }) {
    const pnl = project.pnl || {};
    const burn = pnl.burn_pct ?? 0;
    const marginColor = pnl.margin_pct == null ? "var(--rom-text)" : pnl.margin_pct < 0 ? "#dc2626" : pnl.margin_pct < 15 ? "#d97706" : "var(--rom-green)";
    const burnColor = burn > 100 ? "#dc2626" : burn > 85 ? "#d97706" : "var(--rom-green)";
    return (
        <div className="rom-card" data-testid="pnl-strip">
            <div className="rom-card-body space-y-4">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">Live margin</div>
                        <div className="font-display font-black text-4xl tracking-tight mt-1" style={{ color: marginColor }} data-testid="pnl-margin">
                            {pnl.margin >= 0 ? "" : "−"}{formatCurrency(Math.abs(pnl.margin || 0))}
                            {pnl.margin_pct != null && <span className="text-xl ml-2 align-middle">· {pnl.margin_pct}%</span>}
                        </div>
                        <div className="text-xs text-[var(--rom-text-3)] mt-1">Revenue {formatCurrency(pnl.revenue)} − cost to date {formatCurrency(pnl.cost_total)}</div>
                    </div>
                    <div className="w-full sm:w-64">
                        <div className="flex justify-between text-[11px] text-[var(--rom-text-2)] mb-1"><span>Budget burn</span><span className="font-semibold">{burn}%</span></div>
                        <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, burn)}%`, background: burnColor }} /></div>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 pt-3 border-t border-[var(--rom-border)]">
                    <MiniStat label="ROM value" value={formatCurrency(project.rom_value)} />
                    <EditableMini label="Contract value" value={project.contract_value} onSave={(v) => onSave({ contract_value: v })} testId="val-contract" disabled={!canEdit} />
                    <MiniStat label="Labour (timesheets)" value={formatCurrency(pnl.cost_labour)} sub={pnl.cost_labour_approved != null ? `${formatCurrency(pnl.cost_labour_approved)} approved` : null} accent="orange" />
                    <MiniStat label="Sub-con invoices" value={formatCurrency(pnl.cost_subs)} accent="orange" />
                    <MiniStat label="Equipment hire" value={formatCurrency(pnl.cost_equipment)} accent="orange" />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <EditableMini label="Invoiced" value={project.invoiced_value} onSave={(v) => onSave({ invoiced_value: v })} testId="val-invoiced" disabled={!canEdit || project.invoice_count > 0} />
                    <EditableMini label="Paid" value={project.paid_value} onSave={(v) => onSave({ paid_value: v })} testId="val-paid" disabled={!canEdit || project.invoice_count > 0} />
                    <MiniStat label="Outstanding AR" value={formatCurrency(Math.max(0, (project.invoiced_value || 0) - (project.paid_value || 0)))} accent={((project.invoiced_value || 0) - (project.paid_value || 0)) > 0 ? "navy" : undefined} />
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, sub, accent }) {
    const color = accent === "orange" ? "var(--rom-orange-dark)" : accent === "navy" ? "var(--rom-navy)" : "var(--rom-text)";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            <div className="font-display font-bold text-lg mt-0.5" style={{ color }}>{value}</div>
            {sub && <div className="text-[10px] text-[var(--rom-text-3)]">{sub}</div>}
        </div>
    );
}

function EditableMini({ label, value, onSave, testId, disabled }) {
    const [v, setV] = useState(value || 0);
    useEffect(() => { setV(value || 0); }, [value]);
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            {disabled ? (
                <div className="font-display font-bold text-lg mt-0.5">{value ? formatCurrency(value) : "—"}</div>
            ) : (
                <input data-testid={testId} type="number" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== (value || 0) && onSave(Number(v) || 0)}
                    className="mt-0.5 w-full font-display font-bold text-lg bg-transparent border-b border-dashed border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none" />
            )}
        </div>
    );
}

function ValueCard({ label, value, raw }) {
    return (
        <div className="rom-card"><div className="rom-card-body">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">{label}</div>
            <div className="mt-2 font-display font-black text-2xl tracking-tight">{raw != null ? raw : (value ? formatCurrency(value) : "—")}</div>
        </div></div>
    );
}
